import {
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  columnTypes,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  ExportMenuItems,
  filterByCellValueMenuItem,
  FIXING_INDEX,
  FixingIndexRow,
  FormControlSizes,
  getShowJSONContextItem,
  IconName,
  useAccordionFilterBuilder,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useGetDefaultContextMenuItems,
  useJsonModal,
  useMixpanel,
  usePersistedBlotterTable,
  useWsBlotterTable,
  type BlotterTableSort,
  type Column,
  type FixingIndex,
  type MinimalSubscriptionResponse,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams, RowDoubleClickedEvent } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { useFixingIndicesColumns } from './useFixingIndicesColumns';
import { colIDToFilterBuilderKey, useFixingIndicesFilter, type FixingIndicesFilter } from './useFixingIndicesFilter';

const DEFAULT_SORT: BlotterTableSort<FixingIndex> = '-Timestamp';

export interface FixingIndicesBlotterTableProps {
  blotterID: string;
  blotterPersistID?: string;
  tabLabel?: string;
  defaultColumns?: (Column | keyof FixingIndex)[];
  defaultFilter?: FixingIndicesFilter;
  defaultSort?: BlotterTableSort<FixingIndex>;
  persistFilter?: boolean;
  persistSort?: boolean;
  initialIsOpen?: boolean;
  onCloneTab?: () => void;
  onRowDoubleClicked?: (event: RowDoubleClickedEvent<FixingIndex>) => void;
}

export function FixingIndicesBlotterTable({
  blotterID,
  blotterPersistID = blotterID,
  tabLabel,
  defaultColumns,
  defaultFilter,
  defaultSort = DEFAULT_SORT,
  persistFilter,
  initialIsOpen,
  onCloneTab,
  onRowDoubleClicked,
}: FixingIndicesBlotterTableProps) {
  const mixpanel = useMixpanel();

  const columns = useFixingIndicesColumns({ defaultColumns });
  const persistedBlotterTable = usePersistedBlotterTable(
    blotterPersistID,
    { filter: defaultFilter },
    { persistFilter }
  );

  const filterResults = useFixingIndicesFilter({
    persistedBlotterTable,
  });
  const { clientSideFilter: clientLocalFilter, blotterTableFilterProps, filterBuilderProps } = filterResults;

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps,
  });
  const { handleClickJson, jsonModal } = useJsonModal();
  const getDefaultContextMenuItems = useGetDefaultContextMenuItems();

  const getContextMenuItems = useCallbackRef((params: GetContextMenuItemsParams<FixingIndex>) => {
    const rightClickedRow = params.node?.data;
    if (rightClickedRow == null) {
      return [];
    }

    return compact([
      ...filterByCellValueMenuItem({
        params,
        filterableProperties: filterBuilderProps.properties,
        openClause: filterBuilderAccordion.openClause,
        colIDToFilterBuilderKey,
        mixpanel,
      }),
      getShowJSONContextItem({ params, handleClickJson }),
      'separator',
      ...getDefaultContextMenuItems(params),
    ]);
  });

  const request = useMemo(
    () => ({
      name: FIXING_INDEX,
      tag: blotterID,
      sort_by: '-Timestamp',
    }),
    [blotterID]
  );

  const autoGroupColumnDef = useMemo(
    () =>
      columnTypes.group({
        type: 'group',
        editable: false,
        hide: false,
        suppressColumnsToolPanel: false,
        params: {
          suppressCount: true,
        },
      }),
    []
  );

  const fixingIndexRowPipe = useMemo(
    () => (source: Observable<MinimalSubscriptionResponse<FixingIndex>>) =>
      source.pipe(
        map(
          (response): MinimalSubscriptionResponse<FixingIndex> => ({
            ...response,
            data: response.data?.map(row => new FixingIndexRow(row)),
          })
        )
      ),
    []
  );

  const blotterTable = useWsBlotterTable({
    request,
    rowID: 'IndexID',
    clientLocalFilter,
    persistence: persistedBlotterTable,
    columns,
    gridOptions: {
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
      rowGroupPanelShow: 'always',
      autoGroupColumnDef,
      getContextMenuItems,
      onRowDoubleClicked,
    },
    exportOptions: {
      name: 'FixingIndices',
      tabLabel,
    },
    pipe: fixingIndexRowPipe,
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  const handleCloneTab = useCallbackRef(() => {
    onCloneTab?.();
    extrasMenuPopover.close();
  });

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTableFilterProps}
        {...blotterTable.blotterTableFiltersProps}
        showDateRangePicker={false}
        suffix={
          <BlotterTableExtrasMenu {...extrasMenuPopover}>
            <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />
            {onCloneTab && (
              <Button
                startIcon={IconName.Duplicate}
                variant={ButtonVariants.Default}
                size={FormControlSizes.Small}
                onClick={handleCloneTab}
              >
                Clone Tab
              </Button>
            )}
          </BlotterTableExtrasMenu>
        }
      />
      <BlotterTable {...blotterTable} />
      {jsonModal}
    </>
  );
}
